import { uniq } from 'es-toolkit';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';

import { isTruss } from '@paragon/contract-library';
import { closeCurrentPopup } from '@paragon/popup-state';
import { BundlePositioningView, InstanceInfo, InstancePositions } from '@paragon/production-app-utilities';

import { markBundleFinishedAssembly } from '../../store/actions/production';
import { selectItemByGuid } from '../../store/actions/selectActions';
import { getActiveStation } from '../../store/selectors/getActiveStation';
import { getComponentSpans } from '../../store/selectors/getComponentSpansSelector';
import { getDistanceFormatterSelector } from '../../store/selectors/getDistanceFormatterSelector';
import { getProductionAnalysisSetComponentDesignByAnalysisSetGuid } from '../../store/selectors/getProductionAnalysisSetComponentDesignByAnalysisSetGuid';
import { useAppDispatch, useAppSelector } from '../hooks';

interface BundleLayoutProps {
  readonly bundleGuid: string;
  readonly advanceToNextBundle: () => void;
  readonly setErrorMarkingFinished: (error: boolean) => void;
}

export function BundleLayout({ bundleGuid, advanceToNextBundle, setErrorMarkingFinished }: BundleLayoutProps) {
  const dispatch = useAppDispatch();
  const orderedProductionComponentInstancesByBundleGuid = useAppSelector(
    (state) => state.productionModels.orderedProductionComponentInstancesByBundleGuid,
  );

  const productionAnalysisSetComponentDesignByAnalysisSetGuid = useAppSelector(
    getProductionAnalysisSetComponentDesignByAnalysisSetGuid,
  );
  const componentSpans = useAppSelector(getComponentSpans);
  const station = useAppSelector(getActiveStation);
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);

  const productionComponentInstancesForBundle = useMemo(
    () => orderedProductionComponentInstancesByBundleGuid[bundleGuid],
    [orderedProductionComponentInstancesByBundleGuid, bundleGuid],
  );
  const instancePositions = useMemo(
    () =>
      productionComponentInstancesForBundle.reduce((instancePositions, productionComponentInstance) => {
        instancePositions[productionComponentInstance.guid] = {
          x: productionComponentInstance.bundlePositionX!,
          y: productionComponentInstance.bundlePositionY!,
        };
        return instancePositions;
      }, {} as InstancePositions),
    [productionComponentInstancesForBundle],
  );
  const instanceInfo = useMemo(
    () =>
      productionComponentInstancesForBundle.reduce((instanceInfo, instance) => {
        const componentDesign = productionAnalysisSetComponentDesignByAnalysisSetGuid[instance.analysisSetGuid];
        if (!componentDesign) return instanceInfo;
        const { component } = componentDesign;
        if (!isTruss(component)) return instanceInfo;
        const thickness = component.numberOfPlies * component.thickness;
        instanceInfo[instance.guid] = {
          name: component.name,
          thickness,
          width: componentSpans[componentDesign.guid]!.overallLength,
          plies: component.numberOfPlies,
          analysisSetGuid: instance.analysisSetGuid,
        };
        return instanceInfo;
      }, {} as InstanceInfo),
    [productionComponentInstancesForBundle, productionAnalysisSetComponentDesignByAnalysisSetGuid, componentSpans],
  );

  const markBundleFinishedAssemblyDisabled = useMemo(
    () =>
      uniq(productionComponentInstancesForBundle.map((instance) => instance.status)).every(
        (status) =>
          status === 'FinishedAssembly' || status === 'StoredOnYard' || status === 'Shipped' || status === 'Delivered',
      ),
    [productionComponentInstancesForBundle],
  );

  // From https://stackoverflow.com/a/75112786/4046605
  const bundlePositioningViewContainerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  useEffect(() => {
    if (
      bundlePositioningViewContainerRef.current?.offsetHeight &&
      bundlePositioningViewContainerRef.current?.offsetWidth
    ) {
      setDimensions({
        width: bundlePositioningViewContainerRef.current.offsetWidth,
        height: bundlePositioningViewContainerRef.current.offsetHeight,
      });
    }
  }, []);

  function onClickProductionComponentInstance(productionComponentInstanceGuid: string) {
    dispatch(selectItemByGuid(productionComponentInstanceGuid, 'productionComponentInstance'));
    dispatch(closeCurrentPopup());
  }

  return (
    <Box sx={{ m: 2, display: 'flex', flexDirection: 'column', flex: '1 1 auto', justifyContent: 'center' }}>
      <Box
        ref={bundlePositioningViewContainerRef}
        sx={{ width: '100%', height: '100%', minHeight: '100px', flex: '1 1 auto' }}
      >
        <BundlePositioningView
          width={dimensions.width}
          height={dimensions.height}
          instancePositions={instancePositions}
          instanceInfo={instanceInfo}
          eventHandlers={{ onClick: onClickProductionComponentInstance }}
          distanceFormatter={distanceFormatter}
        />
      </Box>
      {station !== null && station.role === 'Bundler' && (
        <Button
          variant="outlined"
          sx={{ my: 2 }}
          onClick={() => {
            dispatch(markBundleFinishedAssembly(bundleGuid)).then((success) => void setErrorMarkingFinished(!success));
            advanceToNextBundle();
          }}
          disabled={markBundleFinishedAssemblyDisabled}
        >
          Mark Finished Assembly (spacebar)
        </Button>
      )}
    </Box>
  );
}
